// Concierge — small heuristic helpers for routing students toward Bookmate.
// Stage 1: Bookmate only. Stage 2 adds Workflow.
//
// All matching is case-insensitive and operates on the trimmed user message.
// Keep this module pure (no React, no store imports) so it stays trivial to test.

const BOOKMATE_TRIGGER_PATTERNS: RegExp[] = [
  // Direct teach / learn requests
  /\bteach me\b/i,
  /\bteach me about\b/i,
  /\blearn (about )?[a-z]/i,
  /\bi (want|need|would like) to learn\b/i,
  /\bi('?m| am) (new to|a beginner (at|in))\b/i,
  /\bguide me (through|on)\b/i,
  /\bwalk me through\b/i,

  // Tutorial framing
  /\btutorial (on|about|for)\b/i,
  /\bmake (me )?a tutorial\b/i,
  /\bbuild (me )?a tutorial\b/i,
  /\bcreate (a |me a )?tutorial\b/i,

  // Exam / interview prep
  /\bprepare (me )?for (an? )?(exam|interview|test)\b/i,
  /\bhelp me (study|prepare) for\b/i,
  /\binterview (prep|preparation) (on|for)\b/i,

  // From-scratch / complete-intro phrasings
  /\bfrom scratch\b/i,
  /\bcomplete (intro|introduction|guide) to\b/i,
  /\bfundamentals of\b/i,
  /\bcrash course (on|in)\b/i,
];

// Workflow triggers: phrases that signal "automate / collect data / produce a file"
// rather than "teach me a concept". Anti-overlap with BOOKMATE is handled in
// ChatOnlyPage: Bookmate trigger is checked first, Workflow only if Bookmate didn't match.
const WORKFLOW_TRIGGER_PATTERNS: RegExp[] = [
  // Find / search / collect
  /\bfind (me )?(the )?top \d+\b/i,
  /\bfind (me )?(a )?(list|dataset|data|table) of\b/i,
  /\bsearch (for|the web for)\b/i,
  /\bcollect (data|information|info) (on|about|from|of)\b/i,
  /\bgather (data|information|info) (on|about|from)\b/i,

  // Scrape
  /\bscrape\b/i,
  /\bextract data (from|out of)\b/i,
  /\bget data (from|out of) (https?:\/\/|www\.)\b/i,

  // Research framing that produces output
  /\bresearch .+ and (create|generate|build|make) (a |the )?(csv|spreadsheet|dataset|file|table)\b/i,
  /\bresearch (top|the top|the best)\b/i,

  // Top-N / comparison framings that yield a list
  /\b(list|show|give me) (the )?top \d+\b/i,
  /\bcompare (the )?top \d+\b/i,

  // Automation / multi-step jobs
  /\bautomate\b/i,
  /\bset up (a )?(workflow|automation|job)\b/i,
  /\brun (this|it) (every|periodically|every day|daily|weekly)\b/i,
  /\bdo (this|it) in steps\b/i,
  /\bmulti[- ]step\b/i,

  // Output-as-file framings
  /\b(create|make|generate|build) (a |the )?(csv|spreadsheet|dataset|excel|table) (of|with|for|from)\b/i,
  /\bgive me (a |the )?(csv|spreadsheet|dataset|table)\b/i,
];

const YES_EXACT_RESPONSES = new Set([
  'yes', 'y', 'yeah', 'yep', 'yup', 'sure', 'ok', 'okay',
  'alright', 'go ahead', 'do it', 'please do', 'sounds good',
  'lets do it', "let's do it", 'please', '✓', '✅',
]);

const YES_STARTER_PATTERNS: RegExp[] = [
  /^yes[,!.\s]/i,
  /^yeah[,!.\s]/i,
  /^yep[,!.\s]/i,
  /^yup[,!.\s]/i,
  /^sure[,!.\s]/i,
  /^ok[,!.\s]/i,
  /^okay[,!.\s]/i,
  /^alright[,!.\s]/i,
  /^go ahead[,!.\s]/i,
  /^do it[,!.\s]/i,
];

/**
 * True if the message looks like a request that warrants a Bookmate suggestion.
 * Used after the chat agent responds — if the original prompt matches, we offer
 * the student a tutorial as a follow-up.
 */
export function detectBookmateTrigger(message: string): boolean {
  const text = (message || '').trim();
  if (!text) return false;
  return BOOKMATE_TRIGGER_PATTERNS.some((re) => re.test(text));
}

/**
 * True if the message looks like a data-automation request that warrants a
 * Workflow suggestion. Caller should check Bookmate trigger FIRST and only
 * fall through to this when Bookmate didn't match (avoids "teach me how to
 * scrape" routing to Workflow).
 */
export function detectWorkflowTrigger(message: string): boolean {
  const text = (message || '').trim();
  if (!text) return false;
  return WORKFLOW_TRIGGER_PATTERNS.some((re) => re.test(text));
}

/**
 * True if the message is an affirmative reply to a pending suggestion.
 * Matches exact short replies AND "yes, but ..." style replies.
 */
export function isYesResponse(message: string): boolean {
  const text = (message || '').trim().toLowerCase();
  if (!text) return false;
  if (YES_EXACT_RESPONSES.has(text)) return true;
  return YES_STARTER_PATTERNS.some((re) => re.test(text));
}

/**
 * Extracts the modifier from a "yes, but ..." style response.
 * Returns null if the message is just a plain yes.
 *
 * Examples:
 *   "yes" -> null
 *   "yes, but make it for beginners" -> "make it for beginners"
 *   "sure - keep it short" -> "keep it short"
 */
export function extractYesModifier(message: string): string | null {
  const text = (message || '').trim();
  if (!text) return null;
  // Strip the leading yes-word and any separator (comma / dash / colon / "but")
  const match = text.match(
    /^(yes|yeah|yep|yup|sure|ok|okay|alright|go ahead|do it)\s*[,:\-—–]\s*(but\s+)?(.+)$/i,
  );
  if (!match) return null;
  const rest = (match[3] || '').trim();
  return rest.length > 0 ? rest : null;
}

/**
 * Builds the prefill prompt for the destination surface, optionally
 * applying a modifier the student supplied with their "yes, but ..." reply.
 */
export function buildPrefillPrompt(originalPrompt: string, modifier: string | null): string {
  const base = (originalPrompt || '').trim();
  if (!modifier) return base;
  return `${base} — ${modifier}`;
}
